import { useMemo, useState } from 'react';
import { language, t } from '../i18n';

/** Current offset of a time zone, e.g. "GMT+3". */
function offsetOf(zone: string, at: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(at);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

/** Every time zone the browser knows, grouped by continent, with its current offset. */
export function TimeZoneSelect({ value, onChange, id }: { value: string; onChange: (zone: string) => void; id?: string }) {
  const groups = useMemo(() => {
    const now = new Date();
    const zones = new Set(Intl.supportedValuesOf('timeZone'));
    zones.add('UTC');
    if (value) zones.add(value);
    const byGroup = new Map<string, { zone: string; label: string }[]>();
    for (const zone of zones) {
      const slash = zone.indexOf('/');
      const group = slash === -1 ? t('Other') : zone.slice(0, slash);
      const place = (slash === -1 ? zone : zone.slice(slash + 1)).replace(/_/g, ' ').replace(/\//g, ' – ');
      const offset = offsetOf(zone, now);
      const list = byGroup.get(group) ?? [];
      list.push({ zone, label: offset ? `${place} (${offset})` : place });
      byGroup.set(group, list);
    }
    // Europe first: that's where most of our people are.
    return [...byGroup.entries()]
      .sort(([a], [b]) => (a === 'Europe' ? -1 : b === 'Europe' ? 1 : a.localeCompare(b)))
      .map(([group, list]) => [group, list.sort((a, b) => a.label.localeCompare(b.label))] as const);
  }, [value]);

  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} id={id}>
      {groups.map(([group, list]) => (
        <optgroup key={group} label={group}>
          {list.map(({ zone, label }) => (
            <option key={zone} value={zone}>
              {label}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

// Codes the browser names that aren't countries.
const NOT_COUNTRIES = new Set(['EU', 'EZ', 'UN', 'QO', 'ZZ', 'XA', 'XB']);

/** Every country (ISO 3166-1 alpha-2), by name. */
export function useCountries(): { code: string; name: string }[] {
  return useMemo(() => {
    const names = new Intl.DisplayNames([language], { type: 'region', fallback: 'none' });
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const countries: { code: string; name: string }[] = [];
    for (const a of letters) {
      for (const b of letters) {
        const code = a + b;
        const name = NOT_COUNTRIES.has(code) ? undefined : names.of(code);
        if (name && name !== code) countries.push({ code, name });
      }
    }
    return countries.sort((x, y) => x.name.localeCompare(y.name));
  }, []);
}

export function CountrySelect({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (code: string) => void;
  testId?: string;
}) {
  const countries = useCountries();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {!value && <option value="">{t('Choose a country')}</option>}
      {countries.map(({ code, name }) => (
        <option key={code} value={code}>
          {name}
        </option>
      ))}
    </select>
  );
}

/** Two-letter country code as a flag emoji. */
export function flag(code: string): string {
  return /^[A-Z]{2}$/.test(code) ? String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0))) : '';
}

/** Several countries, as removable chips plus a list to add more. */
export function CountryListInput({ value, onChange, placeholder }: { value: string[]; onChange: (codes: string[]) => void; placeholder: string }) {
  const countries = useCountries();
  const names = new Map(countries.map((c) => [c.code, c.name]));
  return (
    <div className="chips">
      {value.map((code) => (
        <span key={code} className="chip">
          {flag(code)} {names.get(code) ?? code}
          <button type="button" onClick={() => onChange(value.filter((c) => c !== code))} aria-label={t('Remove {name}', { name: names.get(code) ?? code })}>
            ×
          </button>
        </span>
      ))}
      <select value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])} aria-label={placeholder}>
        <option value="">{placeholder}</option>
        {countries
          .filter((c) => !value.includes(c.code))
          .map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
      </select>
    </div>
  );
}

// Languages the transcription supports well, most relevant first.
const LANGUAGES = [
  'lt', 'en', 'lv', 'et', 'pl', 'it', 'de', 'fr', 'es', 'pt', 'nl', 'sv', 'da', 'no', 'fi', 'ru', 'uk',
  'be', 'cs', 'sk', 'hu', 'ro', 'bg', 'el', 'hr', 'sl', 'sr', 'tr', 'ar', 'he', 'zh', 'ja', 'ko', 'hi',
];

/** Call languages in order, the first being the main one. */
export function LanguageListInput({ value, onChange }: { value: string[]; onChange: (codes: string[]) => void }) {
  const names = useMemo(() => new Intl.DisplayNames([language], { type: 'language' }), []);
  const name = (code: string) => names.of(code) ?? code;
  return (
    <div className="chips" data-testid="region-languages">
      {value.map((code, i) => (
        <span key={code} className="chip">
          {name(code)}
          {i === 0 && <span className="pill is-brand">{t('Main')}</span>}
          <button type="button" onClick={() => onChange(value.filter((c) => c !== code))} aria-label={t('Remove {name}', { name: name(code) })}>
            ×
          </button>
        </span>
      ))}
      <select value="" onChange={(e) => e.target.value && onChange([...value, e.target.value])} aria-label={t('Add a language')}>
        <option value="">{value.length ? t('Add a language') : t('Choose the main language')}</option>
        {LANGUAGES.filter((code) => !value.includes(code))
          .map((code) => ({ code, label: name(code) }))
          .sort((a, b) => a.label.localeCompare(b.label))
          .map(({ code, label }) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
      </select>
    </div>
  );
}

/** Free-text entries as removable chips plus a box to type more (Enter or comma adds). */
export function TextListInput({
  value,
  onChange,
  placeholder,
  label,
  testId,
}: {
  value: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  label: string;
  testId?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const entries = draft
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry && !value.includes(entry));
    if (entries.length) onChange([...value, ...entries]);
    setDraft('');
  };
  return (
    <div className="chips" data-testid={testId}>
      {value.map((entry) => (
        <span key={entry} className="chip">
          {entry}
          <button type="button" onClick={() => onChange(value.filter((v) => v !== entry))} aria-label={t('Remove {name}', { name: entry })}>
            ×
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={add}
        placeholder={value.length ? t('Add another') : placeholder}
        aria-label={label}
      />
    </div>
  );
}
